import React from "react";
import { clsx } from "clsx";
import { Loader2 } from "lucide-react";

export type ButtonVariant = "primary" | "secondary" | "accent" | "outline" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  children: React.ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: "bg-primary text-ink-950 hover:bg-primary-700 focus:ring-ink-950",
  secondary: "bg-secondary text-white hover:bg-secondary-600 focus:ring-secondary-500",
  accent: "bg-accent text-white hover:bg-accent-600 focus:ring-accent-500",
  // Neutral, not brand yellow: yellow text on a light ground is 1.07:1 and
  // the old hover put white text on yellow, so "Apply" in Manage access was
  // unreadable in every state. Yellow belongs on black. bg-white keeps the
  // hover visible on the neutral-50 strips this sits on.
  outline: "border border-neutral-300 bg-white text-ink-900 hover:bg-neutral-100 focus:ring-ink-950",
  ghost: "text-ink-700 hover:bg-ink-50 focus:ring-ink-500",
  danger: "bg-error text-white hover:bg-error-600 focus:ring-error-500",
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-xs",
  md: "px-4 py-2 text-sm",
  lg: "px-6 py-3 text-base",
};

export const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  size = "md",
  loading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  disabled,
  className,
  children,
  ...props
}) => {
  const isDisabled = disabled || loading;

  return (
    <button
      className={clsx(
        "inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-offset-2",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        variantStyles[variant],
        sizeStyles[size],
        fullWidth && "w-full",
        className
      )}
      disabled={isDisabled}
      {...props}
    >
      {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
      {!loading && leftIcon && <span className="mr-2">{leftIcon}</span>}
      {children}
      {!loading && rightIcon && <span className="ml-2">{rightIcon}</span>}
    </button>
  );
};

export default Button;
